import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RosterCalendar from '../components/RosterCalendar';
import { tokenManager } from '../../contexts/AuthContext';
import ManagerRequestView from '../components/ManagerRequestView';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../components/RosterCalendar.css';

const ManagerDashboard = () => {
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState('request');

  useEffect(() => {
        // 設定這一頁專屬的 Browser Tab 標題
        document.title = "更表管理 | Kaka卡卡兒童超跑"; 

        // (Optional) Cleanup: 當離開呢頁時，改返做預設標題
        return () => {
            document.title = "Kaka卡卡兒童超跑";
        };
    }, []);

  // 1. 安全檢查
  useEffect(() => {
    const token = tokenManager.getToken();
    if (!token) {
      navigate('/ssms/login');
    }
  }, [navigate]);

  // 2. Logout 功能
  const handleLogout = () => {
    tokenManager.removeToken();
    navigate('/ssms/login');
  };

  return (
    // 📱 改動 1: 使用 h-screen (固定高度) 同 flex-col，避免整體頁面滾動
    <div className="h-screen bg-gray-100 flex flex-col overflow-hidden">
      
      {/* --- Top Navigation Bar --- */}
      {/* 📱 改動 2: 手機版高度 h-12 (48px)，桌面版 h-16 */}
      <nav className="bg-white shadow z-20 flex-shrink-0 h-12 sm:h-16 relative">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-full">
          <div className="flex justify-between items-center h-full">
            
            {/* Logo / Title */}
            <div className="flex items-center overflow-hidden">
              {/* 🌟 改動：整合標題 */}
              <h1 className="text-base sm:text-xl font-bold text-gray-800 truncate leading-none flex items-center">
                <span className="mr-2">👔</span>
                店長系統
                <span className="mx-2 text-gray-300">|</span>
                <span className="text-indigo-600">更表管理</span>
              </h1>
            </div>
              {/* 🌟 新設計：Manager 功能切換 Dropdown */}
              <div className="relative group">
                {/* 1. Select 本體 */}
                <select
                  value={currentView}
                  onChange={(e) => setCurrentView(e.target.value)}
                  className={`
                    appearance-none 
                    cursor-pointer
                    pl-4 pr-10 py-2 
                    rounded-lg 
                    border 
                    text-sm font-bold 
                    shadow-sm 
                    focus:outline-none focus:ring-2 focus:ring-offset-1 transition-all
                    ${currentView === 'roster' 
                      ? 'bg-white border-indigo-300 text-indigo-700 focus:ring-indigo-500' // 排班模式：深紫
                      : 'bg-white border-purple-300 text-purple-700 focus:ring-purple-500' // 報更模式：紫
                    }
                  `}
                >
                  <option value="roster">🗓️ 正式排班 (Roster)</option>
                  <option value="request">📨 查看報更 (Request)</option>
                </select>

                {/* 2. 右邊的箭頭 Icon */}
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
                  <svg 
                    className={`h-4 w-4 ${currentView === 'roster' ? 'text-indigo-500' : 'text-purple-500'}`} 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>            {/* Right Side Actions */}
            <div className="flex items-center gap-2">
              {/* 📱 改動 4: 手機隱藏 "Hi, 店長"，慳位 */}
              <span className="hidden sm:block text-gray-500 mr-2 text-sm">
                Hi, 店長
              </span>
              
              {/* 📱 改動 5: 優化按鈕，加強點擊感，手機版字體細啲 */}
              <button 
                onClick={handleLogout}
                className="
                  text-red-600 bg-red-50 hover:bg-red-100 
                  border border-red-200 
                  px-3 py-1 rounded-md 
                  text-xs sm:text-sm font-medium 
                  transition-colors"
              >
                登出
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* --- Main Content Area --- */}
      {/* 📱 改動 6: 
          - flex-grow: 佔滿剩餘高度
          - overflow-hidden: 防止出現外層 Scrollbar
          - px-0: 手機版唔好留白邊，盡量用盡寬度
      */}
      <main className="flex-grow w-full max-w-7xl mx-auto sm:px-4 lg:px-8 overflow-hidden relative">
        {/* 注意：因為 RosterCalendar 本身有 h-screen 同 padding，
            建議你去 RosterCalendar.js 移除最外層的 'h-screen' class，
            改成 'h-full'，咁佢就會乖乖地塞滿呢個 <main>
         */}
        <div 
            className="bg-white rounded-xl shadow-lg overflow-hidden flex flex-col" 
            style={{ height: '85vh' }}
        >
            
            {/* 上半部：說明文字 (Header) */}
            {/* 這裡統一設定 Padding (p-4) */}
            <div className="flex-shrink-0 px-4 pt-4 pb-2">
                {currentView === 'request' ? (
                    <div className="text-sm text-gray-600 border-l-4 border-purple-500 pl-3">
                        <strong>說明：</strong> 這是員工的報更請求 (只讀/參考)。
                    </div>
                ) : (
                    <div className="text-sm text-gray-600 border-l-4 border-indigo-500 pl-3">
                        <strong>說明：</strong> 這是正式更表，您可以在此拖拉排班。
                    </div>
                )}
            </div>

            {/* 下半部：日曆內容 */}
            {/* 這裡設定 flex-1 讓它填滿剩餘空間，並加 px-4 pb-4 讓日曆與邊框有距離 */}
            <div className="flex-1 relative min-h-0 px-4 pb-4">
                {currentView === 'request' ? (
                    <ManagerRequestView />
                ) : (
                    <RosterCalendar />
                )}
            </div>

        </div>
      </main>
    </div>
  );
};

export default ManagerDashboard;